import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setMsgAsync, Message } from './notificationsSlice';

// Async actions for fetching users, offices, devices, and home assistants
export const getUsersAsync = createAsyncThunk(
  'admin/getUsers',
  async () => {
    try {
      const response = await axios.get('/api/admin/users');
      return response.data;
    } catch (e) {
      return null;
    }
  }
);

export const getOfficesAsync = createAsyncThunk(
  'admin/getOffices',
  async () => {
    try {
      const response = await axios.get('/api/admin/offices');
      return response.data;
    } catch (e) {
      return null;
    }
  }
);

export const getDevicesAsync = createAsyncThunk(
  'admin/getDevices',
  async () => {
    try {
      const response = await axios.get('/api/admin/devices');
      return response.data;
    } catch (e) {
      return null;
    }
  }
);

export const getHomeAssistantsAsync = createAsyncThunk(
  'admin/getHomeAssistants',
  async () => {
    try {
      const response = await axios.get('/api/admin/home-assistants');
      return response.data;
    } catch (e) {
      return null;
    }
  }
);

// Async actions for creating, updating, and deleting users, offices, devices, and home assistants
export const createUserAsync = createAsyncThunk(
  'admin/createUser',
  async (userData, { dispatch }) => {
    try {
      const response = await axios.post('/api/admin/users', userData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const createOfficeAsync = createAsyncThunk(
  'admin/createOffice',
  async (officeData, { dispatch }) => {
    try {
      const response = await axios.post('/api/admin/offices', officeData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const createDeviceAsync = createAsyncThunk(
  'admin/createDevice',
  async (deviceData, { dispatch }) => {
    try {
      const response = await axios.post('/api/admin/devices', deviceData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const createHomeAssistantAsync = createAsyncThunk(
  'admin/createHomeAssistant',
  async (homeAssistantData, { dispatch }) => {
    try {
      const response = await axios.post('/api/admin/home-assistants', homeAssistantData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

// Other update and delete operations for users, offices, devices, and home assistants (similar to create actions)
export const updateUserAsync = createAsyncThunk(
  'admin/updateUser',
  async (userData, { dispatch }) => {
    try {
      const response = await axios.put(`/api/admin/users/${userData.id}`, userData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const updateOfficeAsync = createAsyncThunk(
  'admin/updateOffice',
  async (officeData, { dispatch }) => {
    try {
      const response = await axios.put(`/api/admin/offices/${officeData.id}`, officeData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const updateDeviceAsync = createAsyncThunk(
  'admin/updateDevice',
  async (deviceData, { dispatch }) => {
    try {
      const response = await axios.put(`/api/admin/devices/${deviceData.id}`, deviceData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const updateHomeAssistantAsync = createAsyncThunk(
  'admin/updateHomeAssistant',
  async (homeAssistantData, { dispatch }) => {
    try {
      const response = await axios.put(`/api/admin/home-assistants/${homeAssistantData.id}`, homeAssistantData);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const deleteUserAsync = createAsyncThunk(
  'admin/deleteUser',
  async (userId, { dispatch }) => {
    try {
      const response = await axios.delete(`/api/admin/users/${userId}`);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const deleteOfficeAsync = createAsyncThunk(
  'admin/deleteOffice',
  async (officeId, { dispatch }) => {
    try {
      const response = await axios.delete(`/api/admin/offices/${officeId}`);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const deleteDeviceAsync = createAsyncThunk(
  'admin/deleteDevice',
  async (deviceId, { dispatch }) => {
    try {
      const response = await axios.delete(`/api/admin/devices/${deviceId}`);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

export const deleteHomeAssistantAsync = createAsyncThunk(
  'admin/deleteHomeAssistant',
  async (homeAssistantId, { dispatch }) => {
    try {
      const response = await axios.delete(`/api/admin/home-assistants/${homeAssistantId}`);
      return response.data;
    } catch (e) {
      console.log(e);
      dispatch(setMsgAsync(new Message(e.response.data).getAction()));
      return null;
    }
  }
);

const initialState = {
  loading: {
    users: false,
    offices: false,
    devices: false,
    homeAssistants: false,
  },
  users: [],
  offices: [],
  devices: [],
  homeAssistants: [],
};

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setUsersLoading: (state, action) => {
      state.loading.users = action.payload;
    },
    setOfficesLoading: (state, action) => {
      state.loading.offices = action.payload;
    },
    setDevicesLoading: (state, action) => {
      state.loading.devices = action.payload;
    },
    setHomeAssistantsLoading: (state, action) => {
      state.loading.homeAssistants = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.users = action.payload;
      })
      .addCase(getOfficesAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.offices = action.payload;
      })
      .addCase(getDevicesAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.devices = action.payload;
      })
      .addCase(getHomeAssistantsAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.homeAssistants = action.payload;
      })
      .addCase(createUserAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.users.push(action.payload);
        state.loading.users = null;
      })
      .addCase(createOfficeAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.offices.push(action.payload);
        state.loading.offices = null;
      })
      .addCase(createDeviceAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.devices.push(action.payload);
        state.loading.devices = null;
      })
      .addCase(createHomeAssistantAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.homeAssistants.push(action.payload);
        state.loading.homeAssistants = null;
      })
      .addCase(updateUserAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) state.users[index] = action.payload;
        state.loading.users = null;
      })
      .addCase(updateOfficeAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const index = state.offices.findIndex(office => office.id === action.payload.id);
        if (index !== -1) state.offices[index] = action.payload;
        state.loading.offices = null;
      })
      .addCase(updateDeviceAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const index = state.devices.findIndex(device => device.id === action.payload.id);
        if (index !== -1) state.devices[index] = action.payload;
        state.loading.devices = null;
      })
      .addCase(updateHomeAssistantAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const index = state.homeAssistants.findIndex(ha => ha.id === action.payload.id);
        if (index !== -1) state.homeAssistants[index] = action.payload;
        state.loading.homeAssistants = null;
      })
      .addCase(deleteUserAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.users = state.users.filter(user => user.id !== action.payload.id);
        state.loading.users = null;
      })
      .addCase(deleteOfficeAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.offices = state.offices.filter(office => office.id !== action.payload.id);
        state.loading.offices = null;
      })
      .addCase(deleteDeviceAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.devices = state.devices.filter(device => device.id !== action.payload.id);
        state.loading.devices = null;
      })
      .addCase(deleteHomeAssistantAsync.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.homeAssistants = state.homeAssistants.filter(ha => ha.id !== action.payload.id);
        state.loading.homeAssistants = null;
      });
  },
});

export const {
  setUsersLoading,
  setOfficesLoading,
  setDevicesLoading,
  setHomeAssistantsLoading,
} = adminSlice.actions;

export const getUsers = (state) => state.admin.users;
export const getOffices = (state) => state.admin.offices;
export const getDevices = (state) => state.admin.devices;
export const getHomeAssistants = (state) => state.admin.homeAssistants;
export const getLoading = (state) => state.admin.loading;

export default adminSlice.reducer;
